import React from "react";
import { classes } from "lib/actions/util";
import { ButtonProps } from "react-html-props";
import { twMerge } from "tailwind-merge";

type ButtonStateStyle = {
  rested: string;
  hover: string;
  disabled: string;
};

export type ButtonTheme =
  | "primary-solid"
  | "primary-outline"
  | "primary-ghost"
  | "red-solid"
  | "red-ghost"
  | "indigo-solid"
  | "purple-solid";

// See TACO UI style guide in figma if you want to see what each style looks like
const ButtonThemeStyles: Record<ButtonTheme, ButtonStateStyle> = {
  "primary-solid": {
    rested: "bg-neutral-800 text-white",
    hover: "hover:bg-neutral-700",
    disabled: "bg-neutral-300 text-white",
  },
  "primary-outline": {
    rested: "border border-neutral-300 text-primary bg-white",
    hover: "hover:bg-neutral-50",
    disabled: "border border-disabled-300 text-disabled bg-white",
  },
  "primary-ghost": {
    rested: "text-neutral-800",
    hover: "hover:bg-neutral-50",
    disabled: "text-disabled",
  },
  "red-solid": {
    rested: "bg-red-500 text-white",
    hover: "hover:bg-red-600",
    disabled: "bg-red-200 text-white",
  },
  "red-ghost": {
    rested: "text-red-500",
    hover: "hover:bg-red-50",
    disabled: "text-disabled",
  },
  "indigo-solid": {
    rested: "bg-indigo-500 text-white",
    hover: "hover:bg-indigo-600",
    disabled: "bg-indigo-200 text-white",
  },
  "purple-solid": {
    rested: "bg-purple-500 text-white",
    hover: "hover:bg-purple-600",
    disabled: "bg-purple-200 text-white",
  },
};

export default function Button({
  ref,
  theme = "primary-solid",
  slim,
  className,
  textCase = "normal-case",
  ...rest
}: ButtonProps & {
  theme?: ButtonTheme;
  slim?: boolean;
  textCase?: "uppercase" | "lowercase" | "normal-case" | "capitalize";
}) {
  const isEnabled = !rest.disabled;
  const themeStyles = ButtonThemeStyles[theme];

  return (
    <button
      ref={ref}
      className={twMerge(
        classes(
          "flex items-center justify-center rounded-md font-medium tracking-wide",
          textCase,
          slim ? "px-6 py-2" : "px-10 py-3",
          isEnabled && classes(themeStyles.rested, themeStyles.hover),
          !isEnabled && classes(themeStyles.disabled, "cursor-default"),
        ),
        className,
      )}
      {...rest}
    >
      {rest.children}
    </button>
  );
}
